<template>
    <div>
        <div class="ratio ratio-4x3 bg-dark rounded overflow-hidden">
            <video
                ref="video"
                class="w-100 h-100 object-fit-cover"
                autoplay
                muted
                playsinline
            ></video>
            <div
                v-if="!scanning"
                class="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center text-white bg-dark px-3 text-center"
            >
                <span>{{ statusText }}</span>
            </div>
            <template v-if="showScanFrame">
                <span class="camera-scan-corner camera-scan-corner--top-start"></span>
                <span class="camera-scan-corner camera-scan-corner--top-end"></span>
                <span class="camera-scan-corner camera-scan-corner--bottom-start"></span>
                <span class="camera-scan-corner camera-scan-corner--bottom-end"></span>
            </template>
        </div>

        <div v-if="devices.length > 1" class="mt-3">
            <label class="form-label" :for="cameraSelectId">Camera</label>
            <select
                :id="cameraSelectId"
                v-model="selectedDeviceId"
                class="form-select"
                @change="restart"
            >
                <option
                    v-for="(device, index) in devices"
                    :key="device.deviceId"
                    :value="device.deviceId"
                >
                    {{ device.label || `Camera ${index + 1}` }}
                </option>
            </select>
        </div>

        <div v-if="errorMessage" class="camera-scanner__error mt-3" role="alert">
            {{ errorMessage }}
        </div>
        <button
            v-if="!scanning"
            type="button"
            class="btn forkto-secondary-action mt-3"
            @click="start"
        >
            <i class="fas fa-camera me-1" aria-hidden="true"></i>
            {{ retryLabel }}
        </button>
    </div>
</template>

<script>
import { markRaw } from "vue";
import { BrowserCodeReader, BrowserMultiFormatReader } from "@zxing/browser";

const BEEP_AUDIO_URL = "/files/forkto/beep.mp3";
let scannerInstance = 0;

export default {
    name: "CameraCodeScanner",
    props: {
        formats: {
            type: Array,
            required: true,
            validator: (formats) => formats.length > 0
        },
        idleText: { type: String, default: "Camera stopped" },
        retryLabel: { type: String, default: "Try camera again" },
        beep: { type: Boolean, default: true },
        showScanFrame: { type: Boolean, default: false }
    },
    emits: ["decoded", "scan-error"],
    expose: ["start", "stop", "restart"],
    data() {
        scannerInstance += 1;
        return {
            cameraSelectId: `code-scanner-camera-${scannerInstance}`,
            reader: null,
            controls: null,
            devices: [],
            selectedDeviceId: "",
            scanning: false,
            accepted: false,
            errorCode: "",
            errorMessage: "",
            scanRunId: 0,
            beepAudio: null,
            beepAudioUnlockId: 0
        };
    },
    computed: {
        statusText() {
            if (this.errorCode === "permission_denied") return "Camera permission was denied";
            if (this.errorCode === "camera_unavailable") return "No available camera";
            if (this.errorCode === "camera_in_use") return "Camera is already in use";
            return this.idleText;
        }
    },
    created() {
        this.beepAudio = this.createBeepAudio();
    },
    mounted() {
        document.addEventListener("visibilitychange", this.onVisibilityChange);
        window.addEventListener("pagehide", this.stop);
    },
    beforeUnmount() {
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
        window.removeEventListener("pagehide", this.stop);
        this.stop();
    },
    methods: {
        createBeepAudio() {
            if (!this.beep || typeof Audio === "undefined") return null;
            const audio = markRaw(new Audio(BEEP_AUDIO_URL));
            audio.preload = "auto";
            return audio;
        },
        getBeepAudio() {
            if (!this.beepAudio) this.beepAudio = this.createBeepAudio();
            return this.beepAudio;
        },
        resetBeepAudio(audio) {
            try {
                audio.pause();
                audio.currentTime = 0;
            } catch {
                // Audio feedback is optional and must never interrupt scanning.
            }
            audio.muted = false;
        },
        unlockBeepAudio() {
            const audio = this.getBeepAudio();
            if (!audio) return;

            const unlockId = this.beepAudioUnlockId + 1;
            this.beepAudioUnlockId = unlockId;
            audio.muted = true;
            const playPromise = audio.play();
            if (!playPromise || typeof playPromise.then !== "function") {
                this.resetBeepAudio(audio);
                return;
            }
            playPromise
                .then(() => {
                    if (this.beepAudioUnlockId === unlockId) this.resetBeepAudio(audio);
                })
                .catch(() => {
                    if (this.beepAudioUnlockId === unlockId) audio.muted = false;
                });
        },
        playBeep() {
            const audio = this.getBeepAudio();
            if (!audio) return;

            this.beepAudioUnlockId += 1;
            audio.muted = false;
            try {
                audio.currentTime = 0;
            } catch {
                // Audio feedback is optional and must never interrupt scanning.
            }
            const playPromise = audio.play();
            if (playPromise && typeof playPromise.catch === "function") {
                playPromise.catch(() => {});
            }
        },
        clearError() {
            this.errorCode = "";
            this.errorMessage = "";
        },
        setCameraError(error) {
            const name = error?.name || "";
            if (name === "NotAllowedError" || name === "SecurityError") {
                this.errorCode = "permission_denied";
                this.errorMessage = "Allow camera access in your device settings, then try again.";
            } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
                this.errorCode = "camera_unavailable";
                this.errorMessage = "No camera was found on this device.";
            } else if (name === "NotReadableError" || name === "TrackStartError") {
                this.errorCode = "camera_in_use";
                this.errorMessage =
                    "The camera is in use by another app. Close it, then try again.";
            } else {
                console.error("Unable to start code scanner", error);
                this.errorCode = "camera_unavailable";
                this.errorMessage =
                    "The camera could not be started. Check its permission and try again.";
            }
            this.$emit("scan-error", { code: this.errorCode });
        },
        chooseInitialCamera(devices) {
            if (
                this.selectedDeviceId &&
                devices.some((device) => device.deviceId === this.selectedDeviceId)
            ) {
                return;
            }
            const rear = devices.find((device) => /back|rear|environment/i.test(device.label));
            this.selectedDeviceId = rear?.deviceId || "";
        },
        videoConstraints() {
            if (this.selectedDeviceId) {
                return {
                    deviceId: { exact: this.selectedDeviceId },
                    width: { ideal: 1280 },
                    height: { ideal: 720 }
                };
            }
            return {
                facingMode: { ideal: "environment" },
                width: { ideal: 1280 },
                height: { ideal: 720 }
            };
        },
        async refreshDevicesFromActiveStream() {
            try {
                this.devices = await BrowserCodeReader.listVideoInputDevices();
                if (!this.selectedDeviceId) {
                    const activeDeviceId = this.$refs.video?.srcObject
                        ?.getVideoTracks?.()[0]
                        ?.getSettings?.().deviceId;
                    this.selectedDeviceId = activeDeviceId || "";
                }
            } catch {
                // Camera enumeration is optional once an active stream exists.
            }
        },
        async start() {
            this.stop();
            const runId = this.scanRunId;
            this.accepted = false;
            this.clearError();
            this.unlockBeepAudio();

            const reader = markRaw(
                new BrowserMultiFormatReader(undefined, {
                    delayBetweenScanAttempts: 100,
                    delayBetweenScanSuccess: 500
                })
            );
            reader.possibleFormats = this.formats;
            this.reader = reader;

            try {
                const devices = await BrowserCodeReader.listVideoInputDevices();
                if (runId !== this.scanRunId) return;
                this.devices = devices;
                this.chooseInitialCamera(devices);
                this.scanning = true;

                const controls = markRaw(
                    await reader.decodeFromConstraints(
                        { video: this.videoConstraints(), audio: false },
                        this.$refs.video,
                        (result) => {
                            if (!result || this.accepted || runId !== this.scanRunId) return;
                            this.accepted = true;
                            const decoded = {
                                text: result.getText(),
                                format: result.getBarcodeFormat?.()
                            };
                            this.playBeep();
                            this.stop();
                            this.$emit("decoded", decoded);
                        }
                    )
                );

                if (runId !== this.scanRunId || this.accepted) {
                    controls.stop();
                    return;
                }
                this.controls = controls;
                await this.refreshDevicesFromActiveStream();
            } catch (error) {
                if (runId !== this.scanRunId) return;
                this.scanning = false;
                this.setCameraError(error);
            }
        },
        stop() {
            this.scanRunId += 1;
            try {
                this.controls?.stop();
            } catch {
                // The camera may already have been released.
            }
            this.controls = null;
            this.reader = null;
            this.scanning = false;
            const stream = this.$refs.video?.srcObject;
            stream?.getTracks?.().forEach((track) => track.stop());
            if (this.$refs.video) this.$refs.video.srcObject = null;
        },
        restart() {
            return this.start();
        },
        onVisibilityChange() {
            if (document.hidden) this.stop();
        }
    }
};
</script>

<style scoped>
.ratio > .camera-scan-corner {
    position: absolute;
    z-index: 1;
    width: 1.75rem;
    height: 1.75rem;
    pointer-events: none;
    border-color: var(--forkto-ink-1);
    border-style: solid;
}

.camera-scanner__error {
    padding: 0.75rem 1rem;
    color: var(--forkto-warn);
    background: var(--forkto-warn-soft);
    border: 1px solid var(--forkto-warn-line);
    border-radius: var(--forkto-radius-control);
    font-size: 0.875rem;
}

.camera-scan-corner--top-start {
    top: 1.5rem;
    right: auto;
    bottom: auto;
    left: 1.5rem;
    border-width: 2px 0 0 2px;
}

.camera-scan-corner--top-end {
    top: 1.5rem;
    right: 1.5rem;
    bottom: auto;
    left: auto;
    border-width: 2px 2px 0 0;
}

.camera-scan-corner--bottom-start {
    top: auto;
    right: auto;
    bottom: 1.5rem;
    left: 1.5rem;
    border-width: 0 0 2px 2px;
}

.camera-scan-corner--bottom-end {
    top: auto;
    right: 1.5rem;
    bottom: 1.5rem;
    left: auto;
    border-width: 0 2px 2px 0;
}
</style>
